// src/pages/Accounting/Receipts/components/ReviewMode/ReviewForm.tsx

import type React from "react";
import type { ReceiptTax } from "../../types";
import { PALETTE } from "../../constants";

export interface ReviewDraft {
  vendorName: string;
  date: string;
  total: number;
  tax: ReceiptTax;
  category: string;
  notes: string;
}

interface ReviewFormProps {
  draft: ReviewDraft;
  onDraftChange: (next: ReviewDraft) => void;
}

export function ReviewForm({ draft, onDraftChange }: ReviewFormProps) {
  const update = <K extends keyof ReviewDraft>(key: K, value: ReviewDraft[K]) => {
    onDraftChange({ ...draft, [key]: value });
  };
  const updateTax = (key: keyof ReceiptTax, value: number | undefined) => {
    onDraftChange({ ...draft, tax: { ...draft.tax, [key]: value } });
  };

  const hasGst = draft.tax.gst !== undefined;
  const hasHst = draft.tax.hst !== undefined;
  const hasPst = draft.tax.pst !== undefined;

  return (
    <>
      <p
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 7,
          fontSize: 13,
          color: PALETTE.ink2,
          margin: "0 0 18px",
          fontWeight: 500,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: PALETTE.vela,
            color: "#fff",
            fontWeight: 700,
            fontSize: 9,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            marginTop: 2,
          }}
        >
          V
        </span>
        <span>
          <strong style={{ color: PALETTE.ink, fontWeight: 600 }}>Vela</strong>{" "}
          extracted these fields from the receipt. Fix anything that looks wrong,
          then approve.
        </span>
      </p>

      <Row>
        <Field label="Vendor">
          <input
            type="text"
            value={draft.vendorName}
            onChange={(e) => update("vendorName", e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Date">
          <input
            type="date"
            value={draft.date}
            onChange={(e) => update("date", e.target.value)}
            style={inputStyle}
          />
        </Field>
      </Row>

      <Row>
        <Field label="Total">
          <input
            type="number"
            step="0.01"
            value={draft.total}
            onChange={(e) => update("total", parseFloat(e.target.value) || 0)}
            style={inputStyle}
          />
        </Field>
        {hasGst && (
          <Field label="GST">
            <input
              type="number"
              step="0.01"
              value={draft.tax.gst ?? 0}
              onChange={(e) =>
                updateTax("gst", parseFloat(e.target.value) || undefined)
              }
              style={inputStyle}
            />
          </Field>
        )}
        {hasHst && (
          <Field label="HST">
            <input
              type="number"
              step="0.01"
              value={draft.tax.hst ?? 0}
              onChange={(e) =>
                updateTax("hst", parseFloat(e.target.value) || undefined)
              }
              style={inputStyle}
            />
          </Field>
        )}
        {hasPst && (
          <Field label="PST">
            <input
              type="number"
              step="0.01"
              value={draft.tax.pst ?? 0}
              onChange={(e) =>
                updateTax("pst", parseFloat(e.target.value) || undefined)
              }
              style={inputStyle}
            />
          </Field>
        )}
      </Row>

      <Field label="Category">
        <input
          type="text"
          value={draft.category}
          onChange={(e) => update("category", e.target.value)}
          placeholder="Pick a category..."
          style={inputStyle}
        />
        <div style={hintStyle}>
          Vela suggested this based on the line items. Change if needed.
        </div>
      </Field>
    </>
  );
}

interface ReviewNotesProps {
  value: string;
  onChange: (value: string) => void;
}
export function ReviewNotes({ value, onChange }: ReviewNotesProps) {
  return (
    <div style={{ marginTop: 18 }}>
      <Field label="Notes (optional)">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Add a note..."
          style={inputStyle}
        />
      </Field>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", gap: 20 }}>{children}</div>;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 16, flex: 1 }}>
      <div
        style={{
          fontSize: 11,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: PALETTE.ink2,
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 0",
  border: 0,
  borderBottom: `1px solid ${PALETTE.line}`,
  background: "transparent",
  fontSize: 14,
  color: PALETTE.ink,
  fontWeight: 600,
  fontFamily: "inherit",
  outline: "none",
};

const hintStyle: React.CSSProperties = {
  fontSize: 11,
  color: PALETTE.ink3,
  fontWeight: 500,
  marginTop: 4,
};